import { AppError, INTERNAL_SERVER_ERROR, RequestError } from '../error';
import { GraphQLError } from 'graphql';
import { createDatadogLogger } from '../datadogRUM/datadogRUM';
import type { Environment } from '../../services/environment';

interface LogContext extends Record<string, unknown> {
  message?: string;
  activity?: string;
}

interface ErrorContext extends LogContext {
  error?: Error;
}

type ApplicationLogContextKeys = 'advertiserId' | 'userId';
type ApplicationLogContext = Record<ApplicationLogContextKeys, unknown>;

interface Logger {
  info: (message: string, context: LogContext) => void;
  error: (message: string, context?: ErrorContext) => void;
  configureApplicationContext: (
    context: Partial<ApplicationLogContext>,
  ) => void;
}

interface LoggerConfig {
  environment: Environment;
  buildVersion?: string;
}

let logger: Logger;

const getLogger = (): Logger => {
  logger = logger ?? createDefaultLogger();
  return logger;
};

const formatErrorMessage = (
  error: Error,
  debugInfo?: Record<string, any>,
): string => {
  if (error instanceof AppError) {
    return `APP_ERROR: ${error.code}: ${error.message}`;
  } else if (error instanceof GraphQLError) {
    return `GRAPHQL_ERROR: ${debugInfo && debugInfo.operationName} : ${
      error.message
    }`;
  } else if (error instanceof RequestError) {
    const statusCode =
      // @ts-ignore because Error type passed in Error defined by apollo which contains status code as well for network errors
      'statusCode' in error ? error.statusCode : INTERNAL_SERVER_ERROR;
    return `REQUEST_ERROR: ${statusCode} : ${error.message}`;
  }
  return `ERROR: ${error.message}`;
};

const createDefaultLogger = (): Logger => ({
  // eslint-disable-next-line no-console
  info: console.info,
  // eslint-disable-next-line no-console
  error: console.error,
  configureApplicationContext: () => {},
});

const initialiseLogger = ({ environment, buildVersion }: LoggerConfig) => {
  switch (environment) {
    case 'production':
    case 'staging':
      logger = createDatadogLogger({ environment, buildVersion });
      break;

    default:
      logger = createDefaultLogger();
  }
};

export type { LogContext, ErrorContext };

export {
  type Logger,
  type LoggerConfig,
  getLogger,
  formatErrorMessage,
  initialiseLogger,
  type ApplicationLogContext,
};
